import { useUser } from '@application/authentication/use_user';
import { withTranslation } from '@application/i18n/with_translation';
import { ButtonArrange, CellulaFormSubmitData, Flex, Form, InputCheckbox, InputCheckboxGroup } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import type { ReactNode } from 'react';
import { useBasket } from '../../hooks/use-basket';

type Participant = NonNullable<ReturnType<typeof useBasket>['basket']>['Participants'][number];

function getParticipantName(participant: Participant) {
	return [participant.GivenName, participant.Surname].filter(Boolean).join(' ') || withTranslation('PW.Common.Anonymous');
}

interface ParticipantsFormProps {
	participants: readonly Participant[];
	defaultAccess: string[];
	submitLabel: ReactNode;
	inputLabel?: string;
	onSubmit: (data: CellulaFormSubmitData) => void | Promise<void>;
	onInvalid?: () => void;
	onAccessChange?: (value: string[]) => void;
	onSubmitClick?: () => void;
}

export function ParticipantsForm({
	participants,
	defaultAccess,
	submitLabel,
	inputLabel,
	onSubmit,
	onInvalid,
	onAccessChange,
	onSubmitClick,
}: ParticipantsFormProps) {
	const user = useUser();
	const { currentlyRepresentedPatient } = useRepresentablePatients();

	const meLabel = user?.commonName
		? withTranslation('View.Booking.ScreenAccess.Option.Me', { name: user.commonName })
		: withTranslation('View.Booking.ScreenAccess.Option.MeShort');

	// The subject(s) of the case (Type === 'Patient') always have access (for now), so they are
	// rendered read-only and checked. When the user is the patient themselves (no represented
	// patient) the subject is "me" and is already shown by the read-only "me" entry above, so it
	// must not be rendered a second time.
	const readOnlyParticipants =
		currentlyRepresentedPatient != null ? participants.filter((participant) => participant.Type === 'Patient') : [];
	const selectableParticipants = participants.filter((participant) => participant.Type !== 'Patient');

	return (
		<Flex grow={1} gap={tokens.spacing.x2}>
			<Form.Root
				options={{
					defaultValues: { access: defaultAccess },
					mode: 'onBlur',
				}}
				onSubmit={onSubmit}
				onInvalid={onInvalid}
			>
				<Flex grow={1} gap={tokens.spacing.x2}>
					<Flex gap={tokens.spacing.x2}>
						<InputCheckboxGroup
							label={inputLabel ?? withTranslation('View.Booking.ScreenAccess.Input.Label.access')}
							variant='outlined'
							required
							name='access'
							onChange={onAccessChange}
						>
							<InputCheckbox label={meLabel} value='me' readOnly checked />
							{readOnlyParticipants.map((participant) => (
								<InputCheckbox
									key={participant.Id}
									label={getParticipantName(participant)}
									value={String(participant.Id)}
									readOnly
									checked
								/>
							))}
							{selectableParticipants.map((participant) => (
								<InputCheckbox key={participant.Id} label={getParticipantName(participant)} value={String(participant.Id)} />
							))}
						</InputCheckboxGroup>
					</Flex>
				</Flex>

				<ButtonArrange>
					<Form.SubmitButton onClick={onSubmitClick}>{submitLabel}</Form.SubmitButton>
				</ButtonArrange>
			</Form.Root>
		</Flex>
	);
}
